import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { SalesOrderPaymentStatus } from '@/lib/enums/SalesOrderPaymentStatus'
import {
  CANCELLED_SALES_ORDER_PAYMENT_FILTER,
  FSN_NETWORK_SALES_ORDER_FULFILLMENT_STATUS_FILTER,
  NEEDS_FSN_NETWORK_SALES_ORDER_FULFILLMENT_STATUS_FILTER,
} from '@/lib/services/sales-orders/sales-order.schema'
import { cn } from '@/lib/utils'
import {
  CircleDollarSign,
  CircleX,
  Clock,
  ListChecks,
  MapPinOff,
  MapPinned,
  Network,
  UserCheck,
  UserMinus,
  Users,
} from 'lucide-react'
import { Fragment } from 'react'
import { DynamicIcon } from '../ui/DynamicIcon'
import {
  SalesOrderAssignmentFilter,
  type SalesOrderFilterOption,
} from './SalesOrderAssignmentFilter'
import type { SalesOrderSearchRecord } from '@/lib/services/sales-orders/sales-order.schema'

type OrderFilters = Pick<
  SalesOrderSearchRecord,
  'payment_filter' | 'fulfillment_type_filter' | 'fulfillment_status_filter' | 'assignment_filter'
>

export type OrderFilterKey = keyof OrderFilters

interface OrderFilterGroup {
  key: OrderFilterKey
  options: SalesOrderFilterOption[]
  sections?: { label: string; options: SalesOrderFilterOption[] }[]
}

export interface OrderFilterSelectProps {
  filters?: OrderFilters
  onValueChange?: (key: OrderFilterKey, value: string) => void
  className?: string
  triggerClassName?: string
  showAssigneeOptions?: boolean
}

export const orderFilterGroups: OrderFilterGroup[] = [
  {
    key: 'payment_filter',
    options: [
      {
        value: 'all',
        label: 'All payments',
        icon: <CircleDollarSign className="size-4" />,
      },
      {
        value: SalesOrderPaymentStatus.OpenBalance.Value,
        label: SalesOrderPaymentStatus.OpenBalance.Name,
        icon: (
          <DynamicIcon iconName={SalesOrderPaymentStatus.OpenBalance.Icon} className="size-4" />
        ),
      },
      {
        value: SalesOrderPaymentStatus.PartiallyPaid.Value,
        label: SalesOrderPaymentStatus.PartiallyPaid.Name,
        icon: (
          <DynamicIcon iconName={SalesOrderPaymentStatus.PartiallyPaid.Icon} className="size-4" />
        ),
      },
      {
        value: SalesOrderPaymentStatus.Paid.Value,
        label: SalesOrderPaymentStatus.Paid.Name,
        icon: <DynamicIcon iconName={SalesOrderPaymentStatus.Paid.Icon} className="size-4" />,
      },
      {
        value: CANCELLED_SALES_ORDER_PAYMENT_FILTER,
        label: 'Cancelled',
        icon: <CircleX className="size-4" />,
      },
    ],
  },
  {
    key: 'fulfillment_type_filter',
    options: [
      {
        value: 'all',
        label: 'All fulfillment types',
        icon: <MapPinned className="size-4" />,
      },
      {
        value: FulfillmentType.InStore.Value,
        label: FulfillmentType.InStore.Name,
        icon: <DynamicIcon iconName={FulfillmentType.InStore.Icon} className="size-4" />,
      },
      {
        value: FulfillmentType.Pickup.Value,
        label: FulfillmentType.Pickup.Name,
        icon: <DynamicIcon iconName={FulfillmentType.Pickup.Icon} className="size-4" />,
      },
      {
        value: FulfillmentType.Delivery.Value,
        label: FulfillmentType.Delivery.Name,
        icon: <DynamicIcon iconName={FulfillmentType.Delivery.Icon} className="size-4" />,
      },
    ],
  },
  {
    key: 'fulfillment_status_filter',
    options: [
      {
        value: 'all',
        label: 'All statuses',
        icon: <ListChecks className="size-4" />,
      },
    ],
    sections: [
      {
        label: 'Fulfillment',
        options: [
          {
            value: SalesOrderFulfillmentStatus.ToDo.Value,
            label: SalesOrderFulfillmentStatus.ToDo.Name,
            icon: (
              <DynamicIcon iconName={SalesOrderFulfillmentStatus.ToDo.Icon} className="size-4" />
            ),
          },
          {
            value: SalesOrderFulfillmentStatus.InProgress.Value,
            label: SalesOrderFulfillmentStatus.InProgress.Name,
            icon: (
              <DynamicIcon
                iconName={SalesOrderFulfillmentStatus.InProgress.Icon}
                className="size-4"
              />
            ),
          },
          {
            value: SalesOrderFulfillmentStatus.ReadyForPickup.Value,
            label: SalesOrderFulfillmentStatus.ReadyForPickup.Name,
            icon: (
              <DynamicIcon
                iconName={SalesOrderFulfillmentStatus.ReadyForPickup.Icon}
                className="size-4"
              />
            ),
          },
          {
            value: SalesOrderFulfillmentStatus.ReadyForDelivery.Value,
            label: SalesOrderFulfillmentStatus.ReadyForDelivery.Name,
            icon: (
              <DynamicIcon
                iconName={SalesOrderFulfillmentStatus.ReadyForDelivery.Icon}
                className="size-4"
              />
            ),
          },
          {
            value: SalesOrderFulfillmentStatus.Out.Value,
            label: 'Out for Delivery',
            icon: (
              <DynamicIcon iconName={SalesOrderFulfillmentStatus.Out.Icon} className="size-4" />
            ),
          },
          {
            value: SalesOrderFulfillmentStatus.Fulfilled.Value,
            label: SalesOrderFulfillmentStatus.Fulfilled.Name,
            icon: (
              <DynamicIcon
                iconName={SalesOrderFulfillmentStatus.Fulfilled.Icon}
                className="size-4"
              />
            ),
          },
        ],
      },
      {
        label: 'Needs attention',
        options: [
          {
            value: 'past_due',
            label: 'Delivery / pickup date passed',
            icon: <Clock className="size-4" />,
          },
        ],
      },
      {
        label: 'FSN Network',
        options: [
          {
            value: NEEDS_FSN_NETWORK_SALES_ORDER_FULFILLMENT_STATUS_FILTER,
            label: 'Outside area',
            icon: <MapPinOff className="size-4" />,
          },
          {
            value: FSN_NETWORK_SALES_ORDER_FULFILLMENT_STATUS_FILTER,
            label: 'With FSN',
            icon: <Network className="size-4" />,
          },
        ],
      },
    ],
  },
  {
    key: 'assignment_filter',
    options: [
      { value: 'all', label: 'All assignments', icon: <Users className="size-4" /> },
      { value: 'unassigned', label: 'Unassigned', icon: <UserMinus className="size-4" /> },
      { value: 'assigned', label: 'Assigned', icon: <UserCheck className="size-4" /> },
    ],
  },
]

export function OrderFilterSelect({
  filters = {},
  onValueChange,
  className,
  triggerClassName,
  showAssigneeOptions = false,
}: OrderFilterSelectProps) {
  return (
    <div className={cn('flex min-w-0 flex-wrap items-center gap-2', className)}>
      {orderFilterGroups.map((group) => {
        if (group.key === 'assignment_filter') {
          return (
            <SalesOrderAssignmentFilter
              key={group.key}
              filters={filters}
              onValueChange={onValueChange}
              options={group.options}
              showAssigneeOptions={showAssigneeOptions}
              triggerClassName={triggerClassName}
            />
          )
        }

        const value = filters[group.key] ?? 'all'
        const isFiltered = value !== 'all'

        return (
          <Select
            key={group.key}
            value={value}
            onValueChange={(nextValue) => onValueChange?.(group.key, nextValue)}
          >
            <SelectTrigger
              shape="pill"
              selected={isFiltered}
              className={cn('w-fit min-w-[8.75rem]', triggerClassName)}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {group.options.map((preset) => (
                <SelectItem key={preset.value} value={preset.value}>
                  {preset.icon}
                  {preset.label}
                </SelectItem>
              ))}
              {group.sections?.map((section) => (
                <Fragment key={section.label}>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel>{section.label}</SelectLabel>
                    {section.options.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>
                        {preset.icon}
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </Fragment>
              ))}
            </SelectContent>
          </Select>
        )
      })}
    </div>
  )
}
